<template>
  <content-subpage
    v-if="isReady"
    class="lesson-review-content-subpage"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="false"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <inline-loader v-if="isLoading" />
      <template v-else>
        <div class="main-header">
          <ellipsis-text class="lesson-title" :text="subtitle" />
          <cv-button class="button add" size="field" @click="handleAddReview">
            <svg-icon name="Plus24" kind="action" />
            {{ t("teacher_reviews_subpage_add_review_button") }}
          </cv-button>
        </div>
        <lesson-forum-topic :topic="topic" :titles="titles" @reload-topic="reloadTopic" />
      </template>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import LessonForumTopicResourceCreateSubpage from "@modules/lesson-review/components/LessonForumTopicResourceCreateSubpage.vue";
import LessonReviewApi from "@apis/lesson.review.topic.api";
import LessonForumTopic from "@modules/lesson-review/components/LessonForumTopic.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { FORUM_TOPIC_RESOURCE_TYPE } from "@modules/lesson-review/lesson.topic.constant.ts";

// Props
interface Props {
  params: {
    uuid: string;
    resourceUuid: string;
    resourceStatus: string;
    resourceDomain: string;
    title: string;
  };
}
const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  actionBack: [];
  teacherReviewCountUpdated: [count: number];
}>();

// i18n
const { t } = useI18n();

// State
const isLoading = ref(false);
const isReady = ref(false);
const paramsData = ref({ ...(props.params ?? {}) });
const titles = ref<string[]>([]);
const topic = ref<unknown>({
  title: "",
  description: "",
  keywords: "",
  canPost: false,
  forumTopicResourceUuid: props.params.uuid,
  type: FORUM_TOPIC_RESOURCE_TYPE.LESSON_TEACHER_REVIEW,
  uuid: "",
  pollOnly: false,
  thumbnail: "",
  imageFile: null,
});

// Computed
const processingId = computed<string | undefined>(() => props.params?.uuid);
const dirtyId = computed<string | undefined>(() => paramsData.value.uuid);
const title = computed<string>(() => t("teacher_reviews_subpage_title"));
const subtitle = computed<string>(() => `${title.value} (${topic.value?.postCount})`);

// Mixins composition
const { hasParentSubpage, actionHidden, actionBack, defaultExpose, visible } = useContentSubpage({
  id: dirtyId.value,
});
const { resetProcessing } = useProcessingState(processingId.value);
const { passiveApiFailHandler } = useErrorHandler();
const contentSubpageInstance = useContentSubpageInstance();

// Methods
const handleAddReview = function () {
  contentSubpageInstance.show(LessonForumTopicResourceCreateSubpage, {
    uuid: props.params?.uuid,
    baseTitle: [...titles.value],
    topic: topic.value,
    addResourceActionBack: () => {
      reloadTopic();
    },
    actionBack: () => {
      emit("actionBack");
    },
  });
};

const reloadTopic = function () {
  isLoading.value = true;
  fetchTopic();
};

const fetchTopic = function () {
  return new Promise(() => {
    LessonReviewApi.fetchLessonTeacherReviewTopic(props.params.uuid).then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      if (response.data.id === 0) {
        createTopic();
      } else {
        topic.value = response.data;
        emit("teacherReviewCountUpdated", response.data.postCount ?? 0);
        isReady.value = true;
        isLoading.value = false;
      }
    }, passiveApiFailHandler);
  });
};

const createTopic = function () {
  const inputParam = {
    ...topic.value,
    resourceDomain: props.params.resourceDomain,
    resourceStatus: props.params.resourceStatus,
  };
  return new Promise(() => {
    LessonReviewApi.createLessonTeacherReviewTopic(inputParam).then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      topic.value = response.data;
      isReady.value = true;
    }, passiveApiFailHandler);
  });
};

// Created
onMounted(() => {
  titles.value.push(paramsData.value.title);
  titles.value.push(title.value);
  fetchTopic();
});

onUnmounted(() => {
  resetProcessing();
  titles.value.pop();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";

.lesson-review-content-subpage {
  .content {
    .main-header {
      span {
        @include carbon--type-style("productive-heading-02", true);
      }
    }
    .action-button {
      bottom: 3rem;
      right: 3rem;
    }
  }
  .lesson-forum-topic-resource {
    .user-avatar {
      margin: 0;
    }
  }
}
</style>
